import React from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { Field, reduxForm } from 'redux-form';
import { connect } from 'react-redux';

import {
    DetailsFormContainer,
    ButtonGroup,
    FormGroupWrapper,
    FullRow,
    HalfRow,
    LegalInfoWrapper,
    Row
} from './Components';
import Button from '../../components/Button';
import TwoGroup from '../../components/TwoGroup';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import InputGroup from '../InputGroup';
import TextareaGroup from '../TextareaGroup';
import { OnSubmitValidator } from '../../containers/DefaultStyles/FormDetails';

import GetUpdatesCheckbox from '../Widget/GetUpdatesCheckbox';
import LegalInfo from '../Widget/LegalInfo';

import {
    relevantRepresentatives,
    joinEnglish
} from '../../utils/localRepresentative/parser';
import { isPortrait } from '../../utils/size';

import { cacheFormData } from '../../utils/formCaching/actions';
import {
    getCachedFullName,
    findCachedValueByActionIdentifier
} from '../../utils/formCaching/helpers';
import submissionErrorHandler from '../../utils/submissionErrorHandler';
import { capitalization, copyFor } from '../../copy/copy';
import { CardTypes } from '../../utils/enums';
import { Placeholders } from '../../utils/fillDynamicText';
import validate from './validations';

const namePlaceholder = () => copyFor('placeholders', 'yourName', 'email');
const escapedNamePlaceholder = () =>
    namePlaceholder().replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

class DetailsForm extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            textAreaFocused: false,
            valid: false,
            errors: submissionErrorHandler(props.currentFormValues, validate),
            messageTextAreaFocused: false
        };
        this.clearSubmitErrors = this.clearSubmitErrors.bind(this);
        this.toggleTextAreaFocused = this.toggleTextAreaFocused.bind(this);
    }

    toggleTextAreaFocused = () => {
        if (!isPortrait() && !this.props.scrollableDesign) {
            setTimeout(
                () => {
                    this.setState({ textAreaFocused: !this.state.textAreaFocused });
                },
                !this.state.textAreaFocused ? 0 : 300
            );
        }
    };

    componentDidMount() {
        this.setState({ errors: [] });
    }

    setSubmitErrors(errors) {
        this.setState({ errors });
    }

    checkForBlurError(name) {
        const errors = submissionErrorHandler(this.props.currentFormValues, validate);
        if (errors.includes(name) && !this.state.errors.includes(name))
            this.setState({ errors: [...this.state.errors, name] });
    }

    clearSubmitErrors(fieldName) {
        if (fieldName && this.state.errors.includes(fieldName)) {
            this.setState({ errors: this.state.errors.filter(e => e !== fieldName) });
        }
    }
    blurName(event, value) {
        if (value && !this.props.isEmailMessageChanged) {
            this.props.change(
                'message',
                this.props.initialValues.message
                    .replace(RegExp(escapedNamePlaceholder(), 'g'), value)
                    .replace(/@localRepresentative/g, this.props.localRepFullName)
            );
        }
        this.checkForBlurError('fullName');
    }

    render() {
        return (
            <DetailsFormContainer autocomplete="on">
                <Row textAreaFocused={this.state.textAreaFocused}>
                    <HalfRow>
                        <Field
                            className={'fs-block'}
                            name="prefix"
                            onBlur={() => this.checkForBlurError('prefix')}
                            submissionError={this.state.errors.includes('prefix')}
                            clearSubmitErrors={this.clearSubmitErrors}
                            component={InputGroup}
                            type="text"
                            label={copyFor(
                                'formFields',
                                'prefix',
                                'email',
                                capitalization.eachWord
                            )}
                            placeholder={copyFor('placeholders', 'prefix', 'email')}
                        />
                    </HalfRow>
                    <HalfRow>
                        <Field
                            className={'fs-block'}
                            name="fullName"
                            submissionError={this.state.errors.includes('fullName')}
                            clearSubmitErrors={this.clearSubmitErrors}
                            component={InputGroup}
                            type="text"
                            label={copyFor(
                                'formFields',
                                'fullName',
                                'email',
                                capitalization.eachWord
                            )}
                            placeholder={copyFor('placeholders', 'fullName', 'email')}
                            onBlur={(event, value) => this.blurName(event, value)}
                        />
                    </HalfRow>
                </Row>
                <Row textAreaFocused={this.state.textAreaFocused}>
                    <FullRow>
                        <Field
                            className={'fs-block'}
                            name="email"
                            onBlur={() => this.checkForBlurError('email')}
                            submissionError={this.state.errors.includes('email')}
                            clearSubmitErrors={this.clearSubmitErrors}
                            component={InputGroup}
                            type="email"
                            label={copyFor(
                                'formFields',
                                'emailAddress',
                                'email',
                                capitalization.eachWord
                            )}
                            placeholder={copyFor('placeholders', 'emailAddress', 'email')}
                        />
                    </FullRow>
                </Row>
                {!this.props.isRecordVideoAction &&
                    <Row>
                        <FullRow>
                            <Field
                                title="message"
                                name="message"
                                onBlur={() => {
                                    this.setState({messageTextAreaFocused: false});
                                    this.checkForBlurError('message');
                                }}
                                onFocus={() => this.setState({messageTextAreaFocused: true})}
                                active={this.state.messageTextAreaFocused}
                                submissionError={this.state.errors.includes('message')}
                                clearSubmitErrors={this.clearSubmitErrors}
                                component={TextareaGroup}
                                scrollableDesign={this.props.scrollableDesign}
                                label={copyFor(
                                    'formFields',
                                    'message',
                                    'email',
                                    capitalization.eachWord
                                )}
                                textAreaFocused={this.state.textAreaFocused}
                                toggleTextAreaFocused={this.toggleTextAreaFocused}
                                onChange={e => {
                                    const templatedVersion = e.target.value
                                        .replace(
                                            `${this.props.formattedReps}`,
                                            Placeholders.LocalRepresentative
                                        )
                                        .replace(
                                            `${this.props.state}`,
                                            Placeholders.StatePlaceholder
                                        )
                                        .replace(
                                            `${this.props.district}`,
                                            Placeholders.DistrictPlaceholder
                                        );
                                    this.props.cacheFormData(
                                        { message: templatedVersion },
                                        this.props.actionIdentifier
                                    );
                                    this.props.onEmailMessageChange();
                                }}
                            />
                        </FullRow>
                    </Row>
                }
                {this.props.isRecordVideoAction
                ? ( <FormGroupWrapper horizontalLayout={true}>
                        <GetUpdatesCheckbox
                            isEU={this.props.endUserLocation.isEU}
                            labelText={this.props.getUpdatesText}
                        />
                        <LegalInfo align="right" />
                    </FormGroupWrapper>)
                    : (<LegalInfoWrapper>
                        <FormGroupWrapper>
                            <GetUpdatesCheckbox
                                sidebar={true}
                                isEU={this.props.endUserLocation.isEU}
                                labelText={this.props.getUpdatesText}
                            />
                            <LegalInfo align="mobile-right" />
                        </FormGroupWrapper>
                    </LegalInfoWrapper>)
                }
                <ButtonGroup
                    noBack={!this.props.isActionChoiceCard && !this.props.isRecordVideoAction}
                    isSubmitting={this.props.isSubmitting}
                >
                    <TwoGroup noBack={!this.props.isActionChoiceCard && !this.props.isRecordVideoAction}>
                        {(this.props.isActionChoiceCard || this.props.isRecordVideoAction) && (
                            <Button
                                style={{
                                    visibility: this.state.textAreaFocused
                                        ? 'hidden'
                                        : 'visible'
                                }}
                                label={copyFor(
                                    'buttons',
                                    'back',
                                    'email',
                                    capitalization.eachWord
                                )}
                                onClick={this.props.onBackFromRepAction}
                                className="middleButton"
                            />
                        )}
                        <OnSubmitValidator
                            onClick={e => {
                                e.preventDefault();
                                const errors = submissionErrorHandler(
                                    this.props.currentFormValues,
                                    validate
                                );
                                if (!this.state.textAreaFocused) {
                                    if (errors.length === 0 && !this.props.invalid) {
                                        if (!this.state.textAreaFocused) {
                                            this.props.handleSubmit();
                                        }
                                    } else {
                                        this.setSubmitErrors(errors);
                                    }
                                }
                            }}
                        >
                            <Button
                                primary={true}
                                label={
                                    this.state.textAreaFocused
                                        ? copyFor(
                                              'buttons',
                                              'done',
                                              'email',
                                              capitalization.eachWord
                                          )
                                        : copyFor(
                                              'buttons',
                                              'send',
                                              'email',
                                              capitalization.eachWord
                                          )
                                }
                                type="submit"
                                className="submitButton"
                                disabled={
                                    this.props.invalid && !this.state.textAreaFocused
                                }
                                isSubmitting={this.props.isSubmitting}
                            />
                        </OnSubmitValidator>
                        <LoadingSpinner />
                    </TwoGroup>
                </ButtonGroup>
            </DetailsFormContainer>
        );
    }
}

DetailsForm.propTypes = {
    actionIdentifier: PropTypes.string.isRequired,
    cacheFormData: PropTypes.func.isRequired,
    change: PropTypes.func.isRequired,
    currentFormValues: PropTypes.object,
    district: PropTypes.string,
    endUserLocation: PropTypes.object.isRequired,
    formattedReps: PropTypes.string,
    getUpdatesText: PropTypes.string,
    handleSubmit: PropTypes.func.isRequired,
    initialValues: PropTypes.object.isRequired,
    invalid: PropTypes.bool.isRequired,
    isActionChoiceCard: PropTypes.bool.isRequired,
    isEmailMessageChanged: PropTypes.bool,
    isSubmitting: PropTypes.bool.isRequired,
    localRepFullName: PropTypes.string,
    onBackFromRepAction: PropTypes.func,
    onEmailMessageChange: PropTypes.func.isRequired,
    scrollableDesign: PropTypes.bool,
    state: PropTypes.string,
    isRecordVideoAction: PropTypes.bool
};

export default compose(
    connect(
        state => {
            const currentCard = state.widget.cards.find(
                c => c.id === state.widget.activeAction.widgetCardId
            );
            const reps = state.localRep.localRepData
                ? relevantRepresentatives(
                      state.widget.activeAction.representativeSearch,
                      state.localRep.localRepData.representative
                  )
                : [];
            const emailableReps = reps.filter(rep => rep.canBeEmailed).slice(0, 3);
            const formattedReps = joinEnglish(
                reps
                    .filter(
                        rep =>
                            rep.canBeEmailed &&
                            state.localRep.hiddenRepresentatives.indexOf(rep.fullName) ===
                                -1
                    )
                    .map(rep => rep.fullName || rep.twitterMention)
            );
            const actionIdentifier = state.widget.activeAction.identifier;

            const cachedName = getCachedFullName(state);
            const cachedMessage = findCachedValueByActionIdentifier(
                state.cachedFormData.message,
                actionIdentifier
            );

            const message =
                state.cachedFormData.message.length > 0 && !!cachedMessage
                    ? (cachedMessage || '')
                          .replace(
                              new RegExp(Placeholders.LocalRepresentative, 'g'),
                              formattedReps
                          )
                          .replace(
                              new RegExp(Placeholders.UserFullNamePlaceholder, 'g'),
                              !!cachedName ? cachedName : namePlaceholder()
                          )
                          .replace(
                              new RegExp(Placeholders.DistrictPlaceholder, 'g'),
                              (state.localRep.localRepData || {}).district ||
                                  'your district'
                          )
                          .replace(
                              new RegExp(Placeholders.StatePlaceholder, 'g'),
                              (state.localRep.localRepData || {}).stateName ||
                                  'your state'
                          )
                    : (state.widget.activeAction.textRaw || '')
                          .replace(
                              new RegExp(Placeholders.LocalRepresentative, 'g'),
                              formattedReps
                          )
                          .replace(
                              new RegExp(Placeholders.UserFullNamePlaceholder, 'g'),
                              !!cachedName ? cachedName : namePlaceholder()
                          )
                          .replace(
                              new RegExp(Placeholders.DistrictPlaceholder, 'g'),
                              (state.localRep.localRepData || {}).district ||
                                  'your district'
                          )
                          .replace(
                              new RegExp(Placeholders.StatePlaceholder, 'g'),
                              (state.localRep.localRepData || {}).stateName ||
                                  'your state'
                          );

            const isRecordVideoAction = state.widget.activeAction.type === 'recordvideo'
            const recordVideoMessage = isRecordVideoAction && 'RecordVideoActionPlaceholder'

            return {
                initialValues: {
                    getUpdates: !(state.widget.endUserLocation || {}).isEU,
                    message: recordVideoMessage || message,
                    prefix: state.cachedFormData.prefix || '',
                    fullName: cachedName,
                    email: state.cachedFormData.email || ''
                },
                formattedReps,
                district: (state.localRep.localRepData || {}).district || 'your district',
                state: (state.localRep.localRepData || {}).stateName || 'your state',
                isActionChoiceCard: currentCard.type === CardTypes.actionChoice,
                getUpdatesText: state.widget.activeAction.subscriptionOptInText,
                actionIdentifier,
                endUserLocation: state.widget.endUserLocation || {},
                localRepFullName: joinEnglish(emailableReps.map(rep => rep.fullName)),
                currentFormValues: (state.form['emailDetailsForm'] || {}).values,
                supportsExpandableTextarea:
                    !state.widget.activeAction.scrollableDesign &&
                    !state.widget.isMobileResolution,
                isRecordVideoAction,
            };
        },
        {
            cacheFormData
        }
    ),
    reduxForm({
        destroyOnUnmount: true,
        form: 'emailDetailsForm',
        enableReinitialize: false,
        validate
    })
)(DetailsForm);
